/** @type {import('tailwindcss').Config} */
export default {
  content: ['./index.html', './src/**/*.{vue,js,ts,jsx,tsx}'],
  safelist: ['md:w-3/5', 'md:w-4/5', 'md:w-5/5'],
  darkMode: 'class',
  theme: {
    extend: {
      colors: {
        primary: {
          DEFAULT: '#002d6d', //600
          50: '#f2f4f8',
          100: '#e5eaf0',
          200: '#bfcada',
          300: '#99abc5',
          400: '#33578a',
          500: '#1b427c',
          600: '#002d6d',
          700: '#002457',
          800: '#001b41',
          900: '#00122c',
          950: '#000916',
        },
        secondary: {
          DEFAULT: '#3fb4e5', // Correspond à 400
          50: '#ecf7fe',
          100: '#e3f1fb',
          200: '#c0e5f7',
          300: '#89d0f0',
          400: '#3fb4e5',
          500: '#229fd5',
          600: '#1480b5',
          700: '#116793',
          800: '#135779',
          900: '#154965',
          950: '#0e2f43',
        },
        tertiary: {
          DEFAULT: '#f28d78', // Correspond à 400
          50: '#fdf5f3',
          100: '#fde7e3',
          200: '#fcd4cc',
          300: '#f8b7a9',
          400: '#f28d78',
          500: '#e7674c',
          600: '#d44b2e',
          700: '#b23c23',
          800: '#933521',
          900: '#7b3021',
          950: '#42160d',
        },
        grey: {
          DEFAULT: '#a8b0c6', // Correspond à 300
          50: '#f2f5f9',
          100: '#e4e8f1',
          200: '#d5dae7',
          300: '#a8b0c6',
          400: '#9099b0',
          500: '#79849f',
          600: '#4f5d7b',
          700: '#394565',
          800: '#293656',
          900: '#172344',
          950: '#0d1935',
        },
        green: {
          DEFAULT: '#27ae60',
          pale: '#73BA98',
          50: '#f1fcf5',
          100: '#defaea',
          200: '#bef4d4',
          300: '#8beab2',
          400: '#52d689',
          500: '#27ae60',
          600: '#1d9c53',
          700: '#1a7b43',
          800: '#1a6139',
          900: '#175031',
          950: '#072c18',
        },
        jordy: {
          DEFAULT: '#5e78f6', // Correspond à 500
          50: '#e9f0ff',
          100: '#dfebff',
          200: '#c5daff',
          300: '#8fb3ff',
          400: '#7d9cfc',
          500: '#5e78f6',
          600: '#4151ea',
          700: '#333ecf',
          800: '#2c36a7',
          900: '#2b3584',
          950: '#191d4d',
        },
        red: {
          DEFAULT: '#e3324b', // Correspond à 600
          50: '#fff1f1',
          100: '#ffe4e4',
          200: '#fecdcf',
          300: '#fca5a9',
          400: '#fa727b',
          500: '#f34051',
          600: '#e3324b',
          700: '#bd1330',
          800: '#9e132e',
          900: '#87142e',
          950: '#4b0614',
        },
        yellow: {
          DEFAULT: '#ffae20', // Correspond à 400
          50: '#fff7e5',
          100: '#ffefc6',
          200: '#ffe196',
          300: '#ffc54a',
          400: '#ffae20',
          500: '#f98a07',
          600: '#dd6402',
          700: '#b74406',
          800: '#94330c',
          900: '#7a2b0d',
          950: '#461402',
        },
        orange: {
          50: '#fef5ee',
          100: '#fde8d7',
          200: '#facdae',
          300: '#f7a97a',
          400: '#f2743a',
          500: '#ef5920',
          600: '#e04016',
          700: '#ba2e14',
          800: '#942618',
          900: '#772217',
          950: '#400e0a',
        },

        detailsBg: 'var(--color-background-details)',

        decouverte: 'var(--color-decouverte)',
        essentiel: 'var(--color-essentiel)',

        purpleGa: 'rgb(var(--color-purple-ga)/<alpha-value>)',
        purpleLightGa: 'rgb(var(--color-purple-light-ga)/<alpha-value>)',
        brownGa: 'rgb(var(--color-brown-ga)/<alpha-value>)',
      },
      fontFamily: {
        inter: ['Inter', 'sans-serif'],
        neulis: ['Neulis', 'sans-serif'],
        sf: ['Inter', 'sans-serif'],
      },
      width: {
        38: '38px',
        42: '42px',
      },
      height: {
        38: '38px',
        42: '42px',
        'content-scroll': 'calc(100vh - 196px)',
      },
      fontSize: {
        xs: ['10px', '15px'],
        sm: ['12px', '18px'],
        base: ['14px', '20px'],
        md: ['16px', '24px'],
        lg: ['18px', '27px'],
        xl: ['20px', '30px'],
        '2xl': ['22px', '33px'],
        '3xl': ['24px', '36px'],
        '4xl': ['30px', '39px'],
      },
      spacing: {
        0: '0px',
        0.5: '2px',
        1: '4px',
        1.5: '6px',
        2: '8px',
        2.5: '10px',
        3: '12px',
        3.5: '14px',
        4: '16px',
        4.5: '18px',
        5: '20px',
        5.5: '22px',
        6: '24px',
        6.5: '26px',
        7: '28px',
        7.5: '30px',
        8: '32px',
        8.5: '24px',
        9: '36px',
        9.5: '38px',
        10: '40px',
        12: '48px',
        16: '64px',
        18: '96px',
        20: '160px',
      },
      borderRadius: {
        none: '0',
        sm: '4px',
        md: '8px',
        lg: '12px',
        xl: '16px',
        '2xl': '20px',
        '3xl': '24px',
        '4xl': '32px',
        full: '9999px',
        6: '6px',
        18: '18px',
      },
      fontWeight: {
        590: 590,
      },
      gridTemplateColumns: {
        // Simple 16 column grid
        14: 'repeat(14, minmax(0, 1fr))',
        first_30: '33% auto',
        first_50: '50% auto',
      },
    },
  },
  plugins: [],
};
